import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import { API, client } from "@/lib/api";
import SEO from "@/components/SEO";
import CitationBlock from "@/components/CitationBlock";
import ShareButtons from "@/components/ShareButtons";
import AccessibleReader from "@/components/AccessibleReader";
import DigestSubscribeForm from "@/components/DigestSubscribeForm";
import DigestSubscribeModal from "@/components/DigestSubscribeModal";
import { Newspaper, ExternalLink, Loader2, ArrowLeft, Printer } from "lucide-react";
import { trackEvent } from "@/lib/ga4";

/**
 * /noticias-juridicas/:slug — detalhe público.
 *
 * Mesma "infraestrutura acadêmica" das páginas de Artigos:
 *   - Cabeçalho com origem (badge), data e título
 *   - Resumo + corpo integral capturado da fonte
 *   - <CitationBlock> (ABNT/APA/BibTeX + QR Code) no topo, para o
 *     pesquisador copiar a referência antes de ler
 *   - <AccessibleReader> (TTS) para leitura por voz, ativo só quando
 *     há corpo HTML/texto extraído (não há fonte primária em áudio)
 *   - <ShareButtons> (WhatsApp, LinkedIn, X, Facebook, Telegram, copiar)
 *   - Botão "Imprimir / salvar PDF" com header e footer estampados via
 *     o `@media print` global (mesmas regras `.no-print` / `.print-only`
 *     usadas pelas páginas de Artigos)
 *   - CTA grande "Leia a matéria completa na fonte oficial: <órgão>"
 *
 * Notícias editoriais (discovered_via=manual) podem ter HTML rico no
 * content — renderizamos com dangerouslySetInnerHTML porque o conteúdo
 * vem do admin autenticado, mesmo padrão do Blog. Notícias scraped têm
 * texto puro.
 */
export default function LegalNewsArticlePage() {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [error, setError] = useState(null);
  // ABNT pre-carregada para estampar a referência formal no rodapé
  // impresso. O CitationBlock interativo no topo do artigo já busca o
  // mesmo payload — este fetch existe só para garantir que o rodapé
  // de impressão tenha a citação mesmo se o usuário nunca abrir o
  // bloco interativo.
  const [abntCitation, setAbntCitation] = useState("");
  // Ref para o corpo da notícia (texto integral). Consumido pelo
  // <AccessibleReader /> para ler innerText no momento do play.
  const newsBodyRef = useRef(null);

  useEffect(() => {
    setNews(null);
    setError(null);
    setAbntCitation("");
    client.get(`/legal-news/${encodeURIComponent(slug)}`)
      .then((r) => setNews(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
  }, [slug]);

  // Fetch ABNT só depois que a notícia carregou, para já termos um
  // slug confirmado. Silencioso em caso de erro — o footer impresso
  // simplesmente cai para a linha do URL.
  useEffect(() => {
    if (!news?.slug) return;
    let cancelled = false;
    fetch(`${API}/legal-news/${encodeURIComponent(news.slug)}/citation`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.citations?.abnt) setAbntCitation(d.citations.abnt); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [news?.slug]);

  const pageUrl = useMemo(() => {
    if (!news?.slug) return "";
    if (typeof window === "undefined") return `/noticias-juridicas/${news.slug}`;
    return `${window.location.origin}/noticias-juridicas/${news.slug}`;
  }, [news?.slug]);

  // URL específica para compartilhamento social. Aponta para o endpoint
  // ``/api/og/legal-news/{slug}`` que devolve HTML com og:* dinâmicos
  // (title, lead, imagem 1200×630, article:published_time). Crawlers
  // do WhatsApp/Telegram/LinkedIn/X leem essas tags e geram um cartão
  // rico — humanos clicam e o meta-refresh do og_html devolve à SPA.
  const shareUrl = useMemo(() => {
    if (!news?.slug) return pageUrl;
    if (typeof window === "undefined") return `/api/og/legal-news/${news.slug}`;
    return `${window.location.origin}/api/og/legal-news/${news.slug}`;
  }, [news?.slug, pageUrl]);

  // Renderers do Markdown — usados apenas no caminho editorial. Marca
  // links externos com target=_blank + rel=noopener (paridade com o
  // BlogPostPage).
  const mdComponents = useMemo(
    () => ({
      a: ({ href, children, ...props }) => {
        const isExternal = /^https?:\/\//i.test(href || "");
        return (
          <a
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            {...props}
          >
            {children}
          </a>
        );
      },
    }),
    [],
  );

  if (error === "not_found") {
    return (
      <div className="min-h-screen pt-28 md:pt-32 pb-20 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20">
        <SEO title="Notícia não encontrada · JurisTube" url="/noticias-juridicas" noindex />
        <div className="max-w-3xl mx-auto text-center py-20">
          <Newspaper className="w-10 h-10 mx-auto mb-4 text-slate-600" />
          <h1 className="font-serif text-2xl font-bold text-white mb-2">Notícia não encontrada</h1>
          <p className="text-sm text-slate-500 mb-6">
            Talvez tenha sido removida ou o endereço esteja incorreto.
          </p>
          <Link to="/noticias-juridicas" className="inline-flex items-center gap-1 text-sky-300 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Voltar para todas as notícias
          </Link>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen pt-28 md:pt-32 pb-20 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…
      </div>
    );
  }

  const color = news.source_color || "#475569";
  const date = news.published_at
    ? new Date(news.published_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
    : "";
  const isEditorial = news.discovered_via === "manual";
  const shareText = `${news.title} — ${news.source_display_name || news.source_org}`;

  return (
    <>
      <DigestSubscribeModal />
      <div className="min-h-screen pt-28 md:pt-32 pb-20 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20">
      <SEO
        title={`${news.title} — ${news.source_display_name || news.source_org || "Notícias Jurídicas"}`}
        description={news.excerpt || news.title}
        url={`/noticias-juridicas/${news.slug}`}
        author={news.source_display_name || news.source_org || undefined}
        noindex
        noindexFollow
      />
      <article className="max-w-3xl mx-auto space-y-6">
        <Link
          to="/noticias-juridicas"
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 no-print"
          data-testid="legal-news-back"
        >
          <ArrowLeft className="w-4 h-4" /> Notícias Jurídicas
        </Link>

        <header className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-white"
              style={{ background: color }}
              data-testid="legal-news-source-badge"
            >
              {news.source_org}
            </span>
            <span className="text-xs text-slate-500">{news.source_display_name}</span>
            {date && (
              <>
                <span className="text-slate-700">·</span>
                <time className="text-xs text-slate-500">{date}</time>
              </>
            )}
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-white leading-tight" data-testid="legal-news-title">
            {news.title}
          </h1>
          {news.excerpt && (
            <p className="text-base text-slate-300 leading-relaxed border-l-2 border-slate-700 pl-4 italic">
              {news.excerpt}
            </p>
          )}
        </header>

        {/* Toolbar — print + share-top + leitor acessível. Toda a barra
            é hidden no print para deixar o handout limpo. */}
        <div className="no-print flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                trackEvent("legal_news_print", {
                  news_slug: news.slug,
                  source_key: news.source_key,
                });
                window.print();
              }
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold transition"
            data-testid="legal-news-print"
            title="Imprime a notícia com cabeçalho do JurisTube e QR Code, ou salve como PDF acadêmico."
          >
            <Printer className="w-3.5 h-3.5" /> Imprimir / salvar PDF
          </button>
        </div>

        {/* Bloco de citação acadêmica — disposto acima do corpo para o
            pesquisador copiar a referência antes mesmo de ler. */}
        <CitationBlock
          endpoint={`/legal-news/${encodeURIComponent(news.slug)}/citation`}
          testidPrefix="citation-legal-news"
          kindLabel="notícia"
          shareUrl={pageUrl}
        />

        {/* Leitor acessível (Web Speech API + modo dislexia). Lê o
            innerText da div alvo (newsBodyRef) — funciona tanto para
            HTML editorial quanto para texto puro do scraper. */}
        <AccessibleReader targetRef={newsBodyRef} title={news.title} />

        {/* Body — texto integral capturado da fonte (sem fotos),
            quebrado em parágrafos para legibilidade.

            * Editoriais (discovered_via=manual): renderizamos com
              ReactMarkdown + remark-gfm — exatamente o mesmo pipeline
              que o BlogPostPage usa para os posts. Isso aceita texto
              puro colado pelo editor (quebra dupla = parágrafo, ``-``
              ou ``1.`` viram listas, ``**negrito**``, links autoclicáveis
              etc.) ALÉM de HTML inline. ``skipHtml=false`` permite
              <p>, <strong> etc. para retrocompatibilidade com
              editoriais antigos que vieram em HTML; tudo passa por
              DOMPurify antes de chegar ao DOM (defesa em profundidade).
            * Scraped (RSS/HTML): texto puro do tribunal, dividido em
              parágrafos por quebra dupla (comportamento original).
        */}
        <div
          ref={newsBodyRef}
          className="conjur-prose max-w-none"
          data-testid="legal-news-body"
        >
          {isEditorial ? (
            <div data-testid="legal-news-body-html">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={mdComponents}
              >
                {DOMPurify.sanitize(news.content || "", {
                  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "h1", "h2", "h3", "h4", "ul", "ol", "li", "a", "blockquote", "img", "hr", "pre", "code"],
                  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "style"],
                  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                })}
              </ReactMarkdown>
            </div>
          ) : (
            <div data-testid="legal-news-body-text">
              {(news.content || "")
                .split(/\n\n+/)
                .filter((p) => p.trim().length > 0)
                .map((p, i) => (
                  <p key={i} className="mb-4">{p}</p>
                ))}
            </div>
          )}
        </div>

        {/* CTA obrigatório — link para a fonte oficial. Hidden no print
            (no PDF acadêmico a URL já aparece no rodapé estampado). */}
        {news.source_url && (
          <a
            href={news.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="no-print inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white shadow-lg transition hover:scale-[1.02]"
            style={{ background: color }}
            data-testid="legal-news-source-cta"
          >
            <ExternalLink className="w-4 h-4" />
            Leia a matéria completa na fonte oficial: {news.source_display_name || news.source_org}
          </a>
        )}

        {/* Botões de compartilhamento. Idêntico ao layout dos artigos
            doutrinários — mesma paridade de UX que o usuário pediu.
            URL = ``/api/og/legal-news/{slug}`` para que WhatsApp/Telegram/
            LinkedIn renderizem cartão rico com og:* dinâmicos. */}
        <section className="border-t border-slate-800 pt-6 no-print">
          <ShareButtons
            url={shareUrl}
            text={shareText}
            testid="legal-news-share"
          />
        </section>

        <p className="text-[11px] text-slate-600 leading-relaxed pt-4 border-t border-slate-800 no-print">
          Conteúdo coletado automaticamente do feed oficial e curado por filtros de relevância
          (decisão, acórdão, súmula, tese fixada etc.). Todos os direitos autorais permanecem
          com a fonte original; o JurisTube apresenta apenas o sumário e o link para a matéria completa.
        </p>

        {/* Banner discreto de inscrição no Digest Semanal. Hidden no
            print para deixar o handout acadêmico limpo. */}
        <section
          className="no-print rounded-2xl border border-sky-500/25 bg-gradient-to-br from-sky-500/[0.04] to-slate-900/40 p-5 sm:p-6"
          data-testid="legal-news-digest-banner"
        >
          <DigestSubscribeForm
            variant="card"
            source="legal-news-banner"
            testidPrefix="legal-news-digest"
          />
        </section>

        {/* Print-only formal reference no fim do artigo. Mesma
            convenção das páginas de artigos: o handout impresso fecha
            com a referência ABNT canônica. */}
        {abntCitation && (
          <div className="print-only print-abnt-ref" aria-hidden="true">
            <div className="print-abnt-ref-title">Referência</div>
            <p className="print-abnt-ref-body">{abntCitation}</p>
          </div>
        )}

        {/* Print-only footer — estampado em toda página impressa para
            o leitor reencontrar o conteúdo online depois. */}
        <div className="print-only print-footer" aria-hidden="true">
          <div className="print-footer-line">Acesso permanente: {pageUrl}</div>
          <div className="print-footer-line print-footer-tag">
            JurisTube.com.br — Acervo Acadêmico Aberto de Direito Brasileiro
          </div>
        </div>
      </article>
    </div>
    </>
  );
}
